import { CHANGE_TRACKING_SOURCE_IDS } from '@changeTracking/utils/changeTrackingRegistry';
import { trackChangeTrackingSources } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast/Toast';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { captureScreenshot } from '@utils/captureScreenshot';
import { handleToast } from '@utils/utils';

import { isRateLimitError, submitProductFeedbackApi } from './services';
import {
  feedbackStore,
  setFeedbackPanelOpen,
  setFeedbackScreenshot,
  setFeedbackStore,
} from './store';
import type { FeedbackTypeT } from './types';

export const takeFeedbackScreenshot = async () => {
  setFeedbackStore({ capturingScreenshot: true });
  const screenshot = await captureScreenshot();
  setFeedbackScreenshot(screenshot);
  setFeedbackStore({ capturingScreenshot: false });
};

// domToBlob blocks the main thread, so hold it off until the drawer has finished animating in.
const CAPTURE_START_DELAY_MS = 400;

export const openFeedbackPanel = (
  feedbackType: FeedbackTypeT = 'Idea',
  componentSource?: ChangeTrackingSource,
) => {
  setFeedbackPanelOpen(feedbackType, componentSource);
  setFeedbackStore({ capturingScreenshot: true });
  window.setTimeout(
    () => void takeFeedbackScreenshot(),
    CAPTURE_START_DELAY_MS,
  );
};

export const startAnotherFeedback = () => {
  setFeedbackScreenshot(undefined);
  setFeedbackStore({
    text: '',
    submittedKey: '',
    rateLimited: false,
    capturingScreenshot: true,
  });
  window.setTimeout(
    () => void takeFeedbackScreenshot(),
    CAPTURE_START_DELAY_MS,
  );
};

export const submitFeedback = async () => {
  if (feedbackStore.submitting) return;

  setFeedbackStore({ submitting: true, rateLimited: false });

  const source = feedbackStore.componentSource;
  if (source !== undefined) {
    trackChangeTrackingSources([source]);
  }

  try {
    const response = await submitProductFeedbackApi({
      feedbackType: feedbackStore.feedbackType,
      text: feedbackStore.text,
      componentSource: source,
      componentSourceId:
        source === undefined ? undefined : CHANGE_TRACKING_SOURCE_IDS[source],
      allowContact: feedbackStore.allowContact,
      screenshot: feedbackStore.screenshot,
    });

    if (!Boolean(response.isSuccess)) {
      setFeedbackStore({ submitting: false });
      handleToast(
        ToastType.Error,
        'We were unable to send your feedback. Please try again.',
      );
      return;
    }

    setFeedbackStore({ submitting: false, submittedKey: response.value });
    handleToast(
      ToastType.Success,
      'Thanks — your feedback was sent to the product team.',
      `Ticket ${response.value}`,
    );
  } catch (error) {
    setFeedbackStore({ submitting: false });

    if (isRateLimitError(error)) {
      setFeedbackStore({ rateLimited: true });
      return;
    }

    handleToast(
      ToastType.Error,
      'We were unable to send your feedback. Please try again.',
    );
  }
};
